import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MarketDetailsResponse, MarketsService, UsersService } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

interface UserOption {
  id: string;
  text: string;
}

@Component({
  selector: 'app-manage-market-users',
  templateUrl: './manage-market-users.component.html',
  styleUrl: './manage-market-users.component.scss',
  host: {
    class: 'app-manage-market-users'
  },
  standalone: false
})
export class ManageMarketUsersComponent extends BaseViewComponent implements OnInit, OnDestroy {

  private readonly destroyedComponent$ = new Subject<void>();
  private selectedUsersById = new Map<string, any>();

  marketId: number = 0;
  market: MarketDetailsResponse | undefined;
  filterOperator = FilterOperator;
  usersList: UserOption[] = [];
  originalUserIds: string[] = [];
  usersTotalRecords: number = 0;
  usersRowsPerPageOption: number = 10;
  isSaving: boolean = false;

  public addUsersToMarketFormGroup: FormGroup = this.fb.group({
    users: [[]]
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private usersService: UsersService,
    private marketsService: MarketsService,
    private fb: FormBuilder
  ) { super(); }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async params => {
        this.marketId = Number(params['marketId']);
        this.selectedUsersById.clear();

        if (this.marketId > 0) {
          await this.get();
          await this.getUsers({
            pageIndex: 1,
            search: ''
          });
        }
      });
  }

  override async get(): Promise<void> {
    if (this.marketId <= 0) {
      return;
    }

    try {
      this.market = await firstValueFrom(this.marketsService.apiMarketsDetailsIdGet(this.marketId));

      this.setAssignedUsers(this.market.users ?? []);
    } catch (error) {
      console.error('Error fetching market data:', error);
    }
  }

  private setAssignedUsers(marketUsers: any[]): void {
    marketUsers.forEach(user => {
      this.selectedUsersById.set(user.id, {
        id: user.id,
        text: user.email ?? user.username ?? user.id
      });
    });

    const selectedUserIds = marketUsers.map(user => user.id);

    this.addUsersToMarketFormGroup.controls['users'].setValue(selectedUserIds);

    this.originalUserIds = [...selectedUserIds];
  }

  onLazyLoadUsers(event: any): void {
    this.getUsers(event);
  }

  async getUsers(event: any): Promise<void> {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, event?.pageIndex ?? 1);
    const pageSize = Number(event?.pageSize) || this.usersRowsPerPageOption;

    this.usersRowsPerPageOption = pageSize;

    const filters: any[] = [
      {
        propertyName: 'isActive',
        operator: this.filterOperator.EQUALS,
        values: ['true']
      },
      {
        propertyName: 'role.roleActions.restrictionType',
        operator: this.filterOperator.IN,
        values: ['Market']
      }
    ];

    if (search) {
      filters.push({
        propertyName: 'username',
        operator: this.filterOperator.CONTAINS,
        values: [search]
      });
    }

    const filter = {
      pageNumber: activePage,
      pageSize: this.usersRowsPerPageOption,
      filters
    };

    try {
      const usersPage = await firstValueFrom(this.usersService.apiUsersFilterPost(filter));

      const users: UserOption[] = [];

      for (const user of usersPage.results ?? []) {
        if (user.id == null) {
          continue;
        }

        // map resp to combo format
        users.push({
          id: user.id,
          text: user.username ?? user.id
        });
      }

      this.usersList = users;

      this.usersList.forEach(user => {
        this.selectedUsersById.set(user.id, user);
      });

      this.usersTotalRecords = usersPage.totalCount ?? 0;
    } catch (error) {
      console.error('Error fetching market users:', error);
      this.usersList = [];
      this.usersTotalRecords = 0;
    }
  }

  getUsersTotalPages(): number {
    const total = Number(this.usersTotalRecords) || 0;
    const pageSize = Number(this.usersRowsPerPageOption) || 10;

    return Math.max(1, Math.ceil(total / pageSize));
  }

  public getSelectedUsers(): any[] {
    const selectedIds: string[] =
      this.addUsersToMarketFormGroup.get('users')?.value ?? [];

    return selectedIds
      .map(id => this.selectedUsersById.get(String(id)))
      .filter(Boolean);
  }

  removeUser(user: any): void {
    const selectedUserIds: string[] = this.addUsersToMarketFormGroup.controls['users'].value ?? [];

    const updatedUserIds = selectedUserIds.filter(id => String(id) !== String(user.id));

    this.addUsersToMarketFormGroup.controls['users'].setValue(updatedUserIds);
  }

  async submitForm(): Promise<void> {
    if (this.addUsersToMarketFormGroup.invalid || this.isSaving) {
      return;
    }

    if (this.marketId <= 0) {
      return;
    }

    const selectedUserIds: string[] = [
      ...new Set<string>(
        (
          this.addUsersToMarketFormGroup.get('users')?.value ?? []
        ).map((id: string) => String(id))
      )
    ];

    const originalUserIds = this.originalUserIds.map(
      id => String(id)
    );

    const userIdsToAdd = selectedUserIds.filter(
      id => !originalUserIds.includes(id)
    );

    const userIdsToRemove = originalUserIds.filter(
      id => !selectedUserIds.includes(id)
    );

    this.isSaving = true;

    try {
      if (userIdsToRemove.length > 0) {
        const removeRequest = {
          userIds: userIdsToRemove
        };

        await firstValueFrom(this.marketsService.apiMarketsIdUsersRemovePut(this.marketId, removeRequest));
      }

      if (userIdsToAdd.length > 0) {
        const addRequest = {
          userIds: userIdsToAdd
        };

        await firstValueFrom(this.marketsService.apiMarketsIdUsersAddPut(this.marketId, addRequest));
      }

      await this.router.navigate(['/markets', this.marketId]);
    } catch (error) {
      console.error('Error updating market users:', error);
      await this.get();
    } finally {
      this.isSaving = false;
    }
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
